import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Users, Store, CheckCircle, XCircle, Clock, DollarSign, Eye, ChevronRight, LogOut, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  
  // Modal states
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setAdminToken(token);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && adminToken) {
      fetchStats();
      fetchPendingVerifications();
      fetchPendingPayouts();
    }
  }, [isLoggedIn, adminToken]);

  const getAdminHeaders = () => ({
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setAdminToken(data.token);
        localStorage.setItem('admin_token', data.token);
        setIsLoggedIn(true);
        toast.success('Admin login successful');
      } else {
        throw new Error(data.detail || 'Login failed');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken(null);
    setIsLoggedIn(false);
    toast.success('Logged out');
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, {
        headers: getAdminHeaders()
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchPendingVerifications = async () => {
    try {
      const response = await fetch(`${API}/admin/pending-tribal-verifications`, {
        headers: getAdminHeaders()
      });
      if (response.ok) {
        setPendingVerifications(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
    }
  };

  const fetchPendingPayouts = async () => {
    try {
      const response = await fetch(`${API}/admin/pending-payouts`, {
        headers: getAdminHeaders()
      });
      if (response.ok) {
        setPendingPayouts(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  };

  const fetchAllVendors = async () => {
    try {
      const response = await fetch(`${API}/admin/all-vendors`, {
        headers: getAdminHeaders()
      });
      if (response.ok) {
        setAllVendors(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const handleVerifyTribalId = async (vendorId, action) => {
    try {
      const response = await fetch(`${API}/admin/verify-tribal-id/${vendorId}/`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({ 
          action, 
          reason: action === 'reject' ? rejectReason : '' 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setSelectedVendor(null);
        setRejectReason('');
        fetchPendingVerifications();
        fetchStats();
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleProcessPayout = async (payoutId, action) => {
    try {
      const response = await fetch(`${API}/admin/process-payout/${payoutId}/`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({ action })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchPendingPayouts();
        fetchStats();
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="font-syne text-3xl font-bold text-white mb-2">Admin Portal</h1>
            <p className="text-stone-400 font-outfit">EntrepreNatives Administration</p>
          </div>
          
          <form onSubmit={handleLogin} className="card-vendor space-y-6">
            <div>
              <Label className="text-stone-300 font-outfit">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field w-full mt-2"
                placeholder="admin@entreprenatives.com"
                required
                data-testid="admin-email"
              />
            </div>
            
            <div>
              <Label className="text-stone-300 font-outfit">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field w-full mt-2"
                placeholder="Enter admin password"
                required
                data-testid="admin-password"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login to Admin'}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-stone-950">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-yellow-400" />
            <span className="font-syne text-xl font-bold text-white">Admin Dashboard</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="text-stone-400 hover:text-white">
            <LogOut className="w-5 h-5 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 pb-24 lg:p-6 lg:pb-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 mb-6 lg:mb-8">
          {[
            { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400' },
            { label: 'Total Vendors', value: stats?.total_vendors || 0, icon: Store, color: 'text-green-400' },
            { label: 'Pending Tribal IDs', value: stats?.pending_tribal_verifications || 0, icon: Clock, color: 'text-yellow-400' },
            { label: 'Total Referrals', value: stats?.total_referrals || 0, icon: DollarSign, color: 'text-purple-400' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              className="card-vendor"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <stat.icon className={`w-8 h-8 ${stat.color} mb-3`} />
              <span className="block text-2xl font-syne font-bold text-stone-100">{stat.value}</span>
              <span className="text-stone-500 text-sm font-outfit">{stat.label}</span>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'tribal-verification', label: 'Tribal ID Verification' },
            { id: 'payouts', label: 'Payout Requests' },
            { id: 'vendors', label: 'All Vendors' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'vendors') fetchAllVendors();
              }}
              className={`px-4 py-2 rounded-full font-outfit text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-yellow-400 text-black font-bold'
                  : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="card-vendor">
              <h2 className="font-syne text-xl font-bold text-stone-100 mb-4">Platform Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-stone-800/50">
                  <span className="text-stone-400 text-sm font-outfit">Total Bookings</span>
                  <span className="block text-2xl font-bold text-white">{stats?.total_bookings || 0}</span>
                </div>
                <div className="p-4 rounded-xl bg-stone-800/50">
                  <span className="text-stone-400 text-sm font-outfit">Total Services</span>
                  <span className="block text-2xl font-bold text-white">{stats?.total_services || 0}</span>
                </div>
                <div className="p-4 rounded-xl bg-stone-800/50">
                  <span className="text-stone-400 text-sm font-outfit">Verified Businesses</span>
                  <span className="block text-2xl font-bold text-green-400">{stats?.federally_recognized_vendors || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tribal-verification' && (
          <div className="space-y-4">
            <div className="card-vendor">
              <h2 className="font-syne text-xl font-bold text-stone-100 mb-4">
                Pending Tribal ID Verifications ({pendingVerifications.length})
              </h2>
              
              {pendingVerifications.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <p className="text-stone-400 font-outfit">No pending verifications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingVerifications.map((vendor) => (
                    <div key={vendor.vendor_id} className="p-4 rounded-xl bg-stone-800/50 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {vendor.profile_image ? (
                          <img src={vendor.profile_image} alt="" className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-stone-700 flex items-center justify-center">
                            <Users className="w-6 h-6 text-stone-500" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-outfit font-medium">{vendor.owner_name || vendor.business_name}</p>
                          <p className="text-stone-400 text-sm">{vendor.user_email}</p>
                          <p className="text-yellow-400 text-sm font-outfit">{vendor.tribal_affiliation}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {vendor.tribal_id_url && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${vendor.tribal_id_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-stone-700 hover:bg-stone-600 text-stone-300"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                        )}
                        
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              onClick={() => setSelectedVendor(vendor)}
                              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                            >
                              Review
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-stone-900 border-stone-800">
                            <DialogHeader>
                              <DialogTitle className="text-white font-syne">Review Tribal ID</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="p-4 rounded-xl bg-stone-800/50">
                                <p className="text-white font-outfit"><strong>Vendor:</strong> {vendor.owner_name}</p>
                                <p className="text-stone-400"><strong>Email:</strong> {vendor.user_email}</p>
                                <p className="text-yellow-400"><strong>Tribe:</strong> {vendor.tribal_affiliation}</p>
                              </div>
                              
                              {vendor.tribal_id_url && (
                                <div>
                                  <p className="text-stone-300 font-outfit mb-2">Uploaded Tribal ID:</p>
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${vendor.tribal_id_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-yellow-400 hover:underline flex items-center gap-2"
                                  >
                                    <Eye className="w-4 h-4" /> View Document
                                  </a>
                                </div>
                              )}
                              
                              <div>
                                <Label className="text-stone-300">Rejection Reason (if rejecting)</Label>
                                <Textarea
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  placeholder="Enter reason for rejection..."
                                  className="input-field w-full mt-2"
                                />
                              </div>
                              
                              <div className="flex gap-3">
                                <Button
                                  onClick={() => handleVerifyTribalId(vendor.vendor_id, 'approve')}
                                  className="flex-1 bg-green-500 hover:bg-green-400 text-white font-bold"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                                </Button>
                                <Button
                                  onClick={() => handleVerifyTribalId(vendor.vendor_id, 'reject')}
                                  variant="outline"
                                  className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10"
                                >
                                  <XCircle className="w-4 h-4 mr-2" /> Reject
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'payouts' && (
          <div className="space-y-4">
            <div className="card-vendor">
              <h2 className="font-syne text-xl font-bold text-stone-100 mb-4">
                Pending Payout Requests ({pendingPayouts.length})
              </h2>
              
              {pendingPayouts.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-16 h-16 text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400 font-outfit">No pending payout requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingPayouts.map((payout) => (
                    <div key={payout.payout_id} className="p-4 rounded-xl bg-stone-800/50 flex items-center justify-between">
                      <div>
                        <p className="text-white font-outfit font-medium">{payout.user_name}</p>
                        <p className="text-stone-400 text-sm">{payout.user_email}</p>
                        <p className="text-green-400 font-mono text-lg">${payout.amount.toFixed(2)}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleProcessPayout(payout.payout_id, 'approve')}
                          className="bg-green-500 hover:bg-green-400 text-white"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" /> Pay
                        </Button>
                        <Button
                          onClick={() => handleProcessPayout(payout.payout_id, 'reject')}
                          variant="outline"
                          className="border-red-500 text-red-400"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'vendors' && (
          <div className="space-y-4">
            <div className="card-vendor">
              <h2 className="font-syne text-xl font-bold text-stone-100 mb-4">
                All Vendors ({allVendors.length})
              </h2>
              
              <div className="space-y-3">
                {allVendors.map((vendor) => (
                  <div key={vendor.vendor_id} className="p-4 rounded-xl bg-stone-800/50 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {vendor.profile_image ? (
                        <img src={vendor.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-stone-700 flex items-center justify-center">
                          <Store className="w-5 h-5 text-stone-500" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-outfit">{vendor.owner_name || vendor.business_name}</p>
                        <p className="text-stone-500 text-sm">{vendor.tribal_affiliation}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {vendor.vendor_type === 'verified' || vendor.ein_number ? (
                        <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                          Verified Business
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium">
                          Startup Vendor
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs ${
                        vendor.membership_level === 'elite' ? 'bg-yellow-400 text-black' :
                        vendor.membership_level === 'premium' ? 'bg-purple-500 text-white' :
                        'bg-stone-700 text-stone-300'
                      }`}>
                        {vendor.membership_level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
